<script setup lang="ts">
import { ref, watch } from 'vue';
import { BChip, BDateTimeField } from '@/design-system';
import { formatDateForDisplay, formatForDateInput } from '@/utils/dateUtils';

type PropsType = {
    mode?: 'chip' | 'input';
    data?: string[];
    size?: 'x-small' | 'small' | 'default' | 'large' | 'x-large';
    showClose?: boolean;
    autofocus?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    mode: 'chip',
    data: () => [],
    size: 'default',
    showClose: false,
    autofocus: false,
});

const emit = defineEmits<{
    select: [value: string[]];
    remove: [];
    click: [];
    'update:menu': [value: boolean];
}>();

const from = ref<string>(props.data[0] ?? '');
const to = ref<string>(props.data[1] ?? '');

watch(
    () => props.data,
    newData => {
        from.value = newData[0] ?? '';
        to.value = newData[1] ?? '';
    },
);

// BDateTimeField emits an ISO instant; store the calendar day it stands for in the
// browser's own timezone. Sending YYYY-MM-DD is what lets the backend expand the
// range to whole days — an ISO instant would arrive shifted by the UTC offset.
const toCalendarDay = (value: string | null): string => (value ? formatForDateInput(value) : '');

const emitWhenComplete = () => {
    if (from.value !== '' && to.value !== '') {
        emit('select', [from.value, to.value]);
        emit('update:menu', false);
    }
};

const handleFromChange = (value: string | null) => {
    from.value = toCalendarDay(value);
    emitWhenComplete();
};

const handleToChange = (value: string | null) => {
    to.value = toCalendarDay(value);
    emitWhenComplete();
};

const handleRemove = (event: Event) => {
    event.stopPropagation();
    emit('remove');
};

const displayText = () => {
    if (from.value === '' || to.value === '') {
        return '';
    }

    return `${formatDateForDisplay(from.value, 'date')} – ${formatDateForDisplay(to.value, 'date')}`;
};
</script>

<template>
    <div
        v-if="props.mode === 'input'"
        class="condition-date-range d-flex align-center"
    >
        <BDateTimeField
            :model-value="from"
            placeholder="DD.MM.YYYY"
            input-type="date"
            density="compact"
            variant="plain"
            :auto-open-picker="props.autofocus"
            :autofocus="props.autofocus"
            :hide-details="true"
            class="condition-date-range__field"
            @change="handleFromChange"
        />

        <span class="condition-date-range__separator">–</span>

        <BDateTimeField
            :model-value="to"
            placeholder="DD.MM.YYYY"
            input-type="date"
            density="compact"
            variant="plain"
            :hide-details="true"
            class="condition-date-range__field"
            @change="handleToChange"
        />
    </div>

    <BChip
        v-else
        variant="flat"
        :size="props.size"
        class="editable-chip editable-chip--value"
        :closable="props.showClose"
        @click.prevent.stop="emit('click')"
        @click:close="handleRemove"
    >
        <span>{{ displayText() }}</span>
    </BChip>
</template>

<style scoped lang="scss">
.condition-date-range {
    &__field {
        min-width: 120px;
    }

    &__separator {
        padding: 0 4px;
        color: rgb(var(--v-theme-grey-500));
    }
}
</style>
